import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Layers } from "lucide-react";
import { PageHead, Wrap, H2, P, Prose, Impact, DontList, CheckList, Disclaimer } from "@/components/Content";
import { CardsShowcase } from "@/components/CardsShowcase";
import { Seo, serviceSchema, breadcrumbSchema } from "@/components/Seo";

const CARD_CATEGORIES = [
  "Pokémon", "Sports", "Baseball", "Football", "Basketball", "Hockey", "Soccer",
  "TCG", "Vintage trading cards", "Modern trading cards", "Other approved collectible cards",
];

export default function CardServices() {
  return (
    <div className="bg-ink min-h-screen pb-24">
      <Seo
        title="Card Services — Cleaning, Prep & Preservation | CardScience HQ"
        description="Professional inspection, responsible cleaning, careful preparation and preservation for Pokémon, sports and vintage trading cards. Individually evaluated. No grade guaranteed."
        path="/card-services"
        jsonLd={[
          serviceSchema({ name: "Card cleaning and grading preparation", description: "Inspection, responsible cleaning, preparation and preservation for collectible cards.", path: "/card-services" }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Card Services", path: "/card-services" }]),
        ]}
      />
      <PageHead
        eyebrow="Card Services"
        title={<>CLEAN. PREP. PRESERVE.</>}
        sub="Professional inspection, responsible cleaning where appropriate, careful preparation and presentation for collectible cards. Services and techniques vary by collectible — a process appropriate for one type of card may not be appropriate for another."
      />

      <CardsShowcase />

      <Wrap className="py-16">
        <Prose>
          <P>CardScience HQ evaluates collectibles individually.</P>
          <P>
            Our work may include appropriate surface cleaning, careful preparation, preservation-oriented
            handling, presentation improvement and collectible-specific evaluation.
          </P>
          <Impact>Our goal is not to manufacture condition that does not exist.</Impact>
          <P>
            Our goal is to help remove appropriate contaminants or presentation issues when doing so can be
            performed responsibly.
          </P>
        </Prose>

        <div className="mt-12 grid md:grid-cols-2 gap-8">
          <div>
            <p className="eyebrow mb-4 flex items-center gap-2"><Layers size={13} className="text-[color:var(--cyan)]" /> Cards We May Accept</p>
            <CheckList items={CARD_CATEGORIES} />
            <p className="text-sm text-[color:var(--chrome-3)] mt-4">Every collectible is evaluated individually. Acceptance is never guaranteed.</p>
          </div>
          <div>
            <p className="eyebrow mb-4">What We Will Not Do</p>
            <DontList items={[
              "We do not trim cards.",
              "We do not recolor cards.",
              "We do not manufacture corners.",
              "We do not add material to conceal damage.",
              "We do not intentionally misrepresent condition.",
              "We do not promise that cleaning will increase monetary value.",
              "We do not promise that cleaning will increase a grade.",
              "We do not promise that a collectible will be accepted by a grading company.",
              "We do not promise PSA 10s.",
              "We do not promise any specific grade from any grading company.",
            ]} />
          </div>
        </div>

        <div className="mt-12">
          <Disclaimer>
            <p>Not every cleaned card is gradeable, and not every card should be graded. Cleaning cannot erase every defect and preparation cannot turn a damaged card into a perfect card.</p>
            <p>CardScience HQ is not affiliated with, endorsed by, sponsored by or approved by PSA, CGC, Beckett, SGC or any other independent grading company unless explicitly stated otherwise.</p>
          </Disclaimer>
        </div>

        <div className="flex flex-wrap gap-3 mt-12">
          <Link to="/submit" className="btn-holo inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">SUBMIT A COLLECTIBLE <ArrowRight size={16} /></Link>
          <Link to="/comic-services" className="btn-ghost inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">COMIC SERVICES</Link>
        </div>
      </Wrap>
    </div>
  );
}
